import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import {
  QuoteChange,
  QuoteChangePercent,
  getTimeLabels,
  isExtendedHours,
  regularSessionQuote,
} from '@benzinga/quotes-ui';
import { AFTER_MARKET, NO_TRADING, REGULAR, PRE_MARKET } from '@benzinga/quotes-manager/constants';
import type { QuoteSessionType, DelayedQuote } from '@benzinga/quotes-manager';
import type { Quote } from '@benzinga/quotes-v3-manager';
import { formatPriceWithCurrency } from '@benzinga/utils/numberUtils';
import { useIsMounted } from '@benzinga/hooks';
import { Icon } from '@benzinga/core-ui';
import type { BaseQuoteProfile } from '../../utils/quote/types';

interface Props {
  isCrypto?: boolean;
  isRealTime?: boolean;
  profile: BaseQuoteProfile;
  quotes: Quote;
}

const quoteHeaderPriceBoxDefaultClassName =
  'quote-header-price-box flex items-center justify-between gap-x-4 p-[14.5px] rounded-md w-full min-h-[80px]';

const getWidthClassByPrice = (price: number | null | undefined, isMain = false): string => {
  const baseWidths = {
    390: isMain ? 'md:w-[390px]' : 'md:w-[415px]',
    420: isMain ? 'md:w-[420px]' : 'md:w-[430px]',
    430: isMain ? 'md:w-[410px]' : 'md:w-[430px]',
    450: isMain ? 'md:w-[430px]' : 'md:w-[450px]',
    480: isMain ? 'md:w-[460px]' : 'md:w-[480px]',
    490: isMain ? 'md:w-[470px]' : 'md:w-[490px]',
    540: isMain ? 'md:w-[520px]' : 'md:w-[540px]',
  };

  const safePrice = price ?? 0;

  const formattedLength = safePrice > 0 && safePrice < 1 ? formatPriceWithCurrency(safePrice, 'USD').length : 0;

  switch (true) {
    case safePrice > 0 && safePrice < 1 && formattedLength <= 7:
      return baseWidths[430];
    case safePrice > 0 && safePrice < 1 && formattedLength <= 9:
      return baseWidths[490];
    case safePrice > 0 && safePrice < 1 && formattedLength > 9:
      return baseWidths[540];
    case safePrice < 100:
      return baseWidths[390];
    case safePrice < 1000:
      return baseWidths[420];
    case safePrice < 10000:
      return baseWidths[450];
    case safePrice < 100000:
      return baseWidths[480];
    default:
      return baseWidths[540];
  }
};

const generatedColoredBoxClassName = (change: number | null, price: number | null | undefined) => {
  const mainWidth = getWidthClassByPrice(price, true);

  return classNames(quoteHeaderPriceBoxDefaultClassName, mainWidth, {
    'bg-bzgray-300 border border-bzgray-500': !change,
    'bg-bzgreen-400/20 border border-green-700/20': change && change > 0,
    'bg-bzred-500/10 border border-red-500/20': change && change < 0,
  });
};

const ET = 'America/New_York';
const SECONDARY_PRICE_BOX_HIDE_START_MINUTES = 16 * 60;
const SECONDARY_PRICE_BOX_HIDE_END_MINUTES = 16 * 60 + 15;

export const shouldHideSecondaryPriceBox = (profile: BaseQuoteProfile): boolean => {
  const session = profile?.schedule?.type;
  const isAfterHoursSession = session === AFTER_MARKET || session === NO_TRADING;

  if (!isAfterHoursSession) {
    return false;
  }

  const nowEt = DateTime.now().setZone(ET);
  const nowEtMinutes = nowEt.hour * 60 + nowEt.minute;

  return nowEtMinutes >= SECONDARY_PRICE_BOX_HIDE_START_MINUTES && nowEtMinutes < SECONDARY_PRICE_BOX_HIDE_END_MINUTES;
};

export const QuoteHeaderPriceBox: React.FC<Props> = ({ isCrypto, isRealTime, profile, quotes }) => {
  const { t } = useTranslation('quote');
  const [isMountedDelayed, setIsMountedDelayed] = useState(false);
  const mounted = useIsMounted();

  useEffect(() => {
    if (!mounted) return;
    const timer = setTimeout(() => {
      setIsMountedDelayed(true);
      // wait 2 seconds before showing the delayed message as socket sometimes takes a bit to connect
    }, 2000);
    return () => clearTimeout(timer);
  }, [mounted]);

  const priceDataNotAvailable = !profile?.richQuoteData?.lastTradePrice && !profile?.richQuoteData?.close;

  return (
    <div
      className={classNames('w-full flex flex-col items-center justify-center', {
        'h-8': priceDataNotAvailable,
        'min-h-[110px]': !priceDataNotAvailable,
      })}
    >
      <div className="flex flex-wrap lg:flex-nowrap gap-2 w-full">
        {priceDataNotAvailable ? (
          <PriceDataUnavailable />
        ) : isCrypto ? (
          <CryptoPriceBox isRealTime={isRealTime} profile={profile} quotes={quotes} />
        ) : (
          <>
            <MainPriceBox isRealTime={isRealTime} profile={profile} quotes={quotes} />
            {!isCrypto && !shouldHideSecondaryPriceBox(profile) && (
              <SecondaryPriceBox profile={profile} quotes={quotes} />
            )}
            {/* {!isCrypto && profile.schedule?.type !== REGULAR && <SecondPriceBox profile={profile} />} */}
          </>
        )}
      </div>
      <div
        className={classNames('w-full whitespace-nowrap text-xs mt-1 text-gray-600 h-[16px]', {
          'mb-2': !profile?.wiim,
        })}
      >
        {!priceDataNotAvailable && !isRealTime && isMountedDelayed && (
          <i>{t('Quote.Market.minutes-delayed', { minutes: 15 })}</i>
        )}
      </div>
    </div>
  );
};

const MainPriceBox: React.FC<Props> = ({ isRealTime, profile, quotes }) => {
  const { t } = useTranslation('quote');
  const values = getMainPriceBoxValues(profile, quotes, isRealTime, t);

  //const ethPrice = profile.richQuoteData.ethPrice;
  // if isExtendedHours use previousClosePrice otherwise use ethPrice
  const className = generatedColoredBoxClassName(values.change, values.price);

  return (
    <div className={className}>
      {typeof values.price === 'number' && <PriceText price={values.price} />}
      <div className="ml-auto min-w-[200px] lg:min-w-[unset]">
        <div className="quote-change-data flex items-center gap-2 text-bzgray-900 text-base min-h-[26px]">
          <QuoteChangeIcon change={values.change} />
          {typeof values.change === 'number' && (
            <QuoteChange absoluteChange={values.change} positiveColor="text-green-600" />
          )}
          {typeof values.changePercent === 'number' && typeof values.change === 'number' && (
            <QuoteHeaderChangePercent change={values.change} changePercent={values.changePercent} />
          )}
        </div>
        <TimeLabel text={values.timeLabel} />
      </div>
    </div>
  );
};

const SecondaryPriceBox: React.FC<Props> = ({ profile, quotes }) => {
  const { t } = useTranslation('quote');
  const values = getSecondaryPriceBoxValues(profile, quotes, t);
  if (!values.price) return null;
  const widthClass = getWidthClassByPrice(values.price, false);
  return (
    <div
      className={`${quoteHeaderPriceBoxDefaultClassName} ${widthClass} bg-bzgray-200 text-bzgray-900 border border-bzgray-400`}
    >
      {typeof values.price === 'number' && <PriceText price={values.price} />}
      <div className="ml-auto min-w-[200px] lg:min-w-[unset]">
        <div className="quote-change-data flex items-center gap-2 text-base min-h-[26px]">
          <QuoteChangeIcon change={values.change} />
          {typeof values.change === 'number' && (
            <QuoteChange absoluteChange={values.change} positiveColor="text-green-600" />
          )}
          {typeof values.changePercent === 'number' && typeof values.change === 'number' && (
            <QuoteHeaderChangePercent change={values.change} changePercent={values.changePercent} />
          )}
        </div>
        <TimeLabel text={values.timeLabel} />
      </div>
    </div>
  );
};

const CryptoPriceBox: React.FC<Props> = ({ isRealTime, profile, quotes }) => {
  const { t } = useTranslation('quote');
  const profileQuote = profile?.richQuoteData;
  const price = isRealTime ? ((quotes as Quote)?.price ?? profileQuote?.lastTradePrice) : profileQuote?.lastTradePrice;
  const change = isRealTime ? (quotes?.change ?? profileQuote?.change ?? null) : (profileQuote?.change ?? null);
  const changePercent = isRealTime
    ? (quotes?.changePercent ?? profileQuote?.changePercent)
    : profileQuote?.changePercent;
  const lastTradeTime = isRealTime
    ? quotes?.time || quotes?.lastTradeTime || profileQuote?.lastTradeTime
    : profileQuote?.lastTradeTime;
  const updateTime = lastTradeTime ? DateTime.fromMillis(lastTradeTime).setZone('UTC').toFormat('h:mm a ZZZZ') : null;
  const timeLabel = lastTradeTime ? `${t('Quote.Price.last-update')}: ${updateTime}` : '-';
  const className = generatedColoredBoxClassName(change as number | null, price);

  return (
    <div className={`${className} whitespace-nowrap`}>
      {typeof price === 'number' && <PriceText price={price} />}
      <div>
        <div className="quote-change-data flex items-center gap-2 text-bzred-500 text-base min-h-[26px]">
          <QuoteChangeIcon change={change} />
          {typeof change === 'number' && <QuoteChange absoluteChange={change} positiveColor="text-green-600" />}
          {typeof changePercent === 'number' && (
            <QuoteHeaderChangePercent change={change} changePercent={changePercent} />
          )}
        </div>
        <TimeLabel text={timeLabel} />
      </div>
    </div>
  );
};

const TimeLabel = ({ text }: { text: string | null }) => {
  // Real-time timestamp: the SSR snapshot and live client value legitimately differ.
  // Suppress the hydration warning for this text node only — no rendering logic changes.
  return (
    <div className="text-bzgray-600 text-xs font-semibold uppercase mt-1 whitespace-nowrap" suppressHydrationWarning>
      {text}
    </div>
  );
};

const PriceDataUnavailable: React.FC = () => {
  return (
    <div className="flex items-center gap-1 text-bzred-500 text-sm font-semibold h-8">
      <Icon className="h-5 w-5 fill-current mt-2" icon={faExclamationTriangle} />
      <span>Price data is not available</span>
    </div>
  );
};

const PriceText = ({ price }: { price: number }) => {
  // Real-time price: the SSR snapshot and the live client value legitimately differ.
  // Suppress the hydration warning for this text node only (client value wins) — the
  // rendering logic and conditional rendering are unchanged from the original.
  return (
    <div className="text-3xl md:text-5xl text-bzgray-900 leading-none" suppressHydrationWarning>
      {formatPriceWithCurrency(price, 'USD')}
    </div>
  );
};

const QuoteHeaderChangePercent = ({ change, changePercent }: { change: number | null; changePercent: number }) => {
  return (
    <span
      className={classNames('px-1 py-[1px] rounded', {
        'bg-bzgray-100': !change,
        'bg-bzgreen-500/20': change && change > 0,
        'bg-bzred-500/10': change && change < 0,
      })}
    >
      <QuoteChangePercent percentageChange={changePercent} positiveColor="text-green-600" />
    </span>
  );
};

const QuoteChangeIcon = ({ change }: { change: number | null }) => {
  if (!change) return null;
  return (
    <div
      className={classNames('flex items-center justify-center p-3 rounded w-5 h-5', {
        'bg-bzgray-100': !change,
        'bg-bzgreen-500/20': change && change > 0,
        'bg-bzred-500/10': change && change < 0,
        'text-bzred-500': change && change < 0,
        'text-green-600': change && change > 0,
      })}
    >
      <span>
        <svg
          className="feather feather-arrow-up-right"
          fill="none"
          height="20"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3"
          viewBox="0 0 24 24"
          width="20"
          xmlns="http://www.w3.org/2000/svg"
        >
          {change > 0 ? (
            <>
              <path d="M7 17L17 7"></path>
              <path d="M7 7L17 7 17 17"></path>
            </>
          ) : (
            <>
              <path d="M7 7L17 17"></path>
              <path d="M17 7L17 17 7 17"></path>
            </>
          )}
        </svg>
      </span>
    </div>
  );
};

export const getMainPriceBoxValues = (
  profile: BaseQuoteProfile,
  quotes: Quote,
  isRealTime?: boolean,
  translateFn?: (key: string, options?: { ns?: string }) => string,
): {
  change: number | null;
  changePercent: number | null;
  price: number | null | undefined;
  timeLabel: string;
} => {
  const close = profile?.richQuoteData?.close;
  const previousClosePrice = profile?.richQuoteData?.previousClosePrice;

  const session = profile?.schedule?.type;
  const isRegular = session === REGULAR;
  const isNoTrading = session === NO_TRADING;
  const isPremarket = session === PRE_MARKET;

  let price: number | null | undefined = null;
  let change: number | null = null;
  let changePercent: number | null = null;
  let timeLabel = '-';

  if (isRegular) {
    price = isRealTime
      ? ((quotes as Quote)?.price ?? quotes?.askPrice)
      : (profile?.richQuoteData?.lastTradePrice ?? profile?.richQuoteData?.close);
    change = price && previousClosePrice ? price - previousClosePrice : (quotes?.change ?? null);
    changePercent =
      change && previousClosePrice ? (change / previousClosePrice) * 100 : (quotes?.changePercent ?? null);
    const quoteData = isRealTime ? (quotes as DelayedQuote) : profile?.richQuoteData;
    timeLabel = quoteData
      ? getTimeLabels(quoteData, session, { isRealTime, longDate: true }, translateFn).timestampClose
      : '-';
  } else if (close && previousClosePrice) {
    price = close;
    change = price - previousClosePrice;
    changePercent = (change / previousClosePrice) * 100;
  } else if (isNoTrading && previousClosePrice) {
    price = previousClosePrice;
    change = quotes?.change ?? null;
    changePercent = quotes?.changePercent ?? null;
  } else if (isPremarket && previousClosePrice) {
    price = previousClosePrice;
    change = quotes?.change ?? null;
    changePercent = quotes?.changePercent ?? null;
  } else if (!close) {
    const [newPrice, newChange, newChangePercent] = regularSessionQuote(
      profile?.richQuoteData?.close || quotes?.close,
      profile?.richQuoteData?.previousClose || quotes?.previousClose,
      profile?.richQuoteData?.lastTradePrice,
    );
    price = newPrice;
    change = newChange ?? null;
    changePercent = newChangePercent ?? null;
  }

  if (price == null) {
    price = close ?? profile?.richQuoteData?.lastTradePrice ?? null;
    if (change == null && typeof price === 'number' && typeof previousClosePrice === 'number') {
      change = price - previousClosePrice;
      changePercent = previousClosePrice !== 0 ? (change / previousClosePrice) * 100 : changePercent;
    }
  }

  if (!(isRegular && isRealTime)) {
    timeLabel = profile?.richQuoteData
      ? getTimeLabels(profile.richQuoteData, session, { longDate: true }, translateFn).timestampClose
      : '-';
  }

  return {
    change,
    changePercent,
    price,
    timeLabel,
  };
};

export const getSecondaryPriceBoxValues = (
  profile: BaseQuoteProfile,
  quotes?: Quote,
  translateFn?: (key: string, options?: { ns?: string }) => string,
): {
  change: number | null;
  changePercent: number | null;
  price: number | null | undefined;
  timeLabel: string | null;
} => {
  let price: number | null | undefined = null;
  let change: number | null = null;
  let changePercent: number | null = null;
  let timeLabel = '-';

  const session = profile?.schedule?.type as QuoteSessionType;
  const isRegular = session === REGULAR;
  const isPremarket = session === PRE_MARKET;

  if (isRegular) {
    return {
      change: null,
      changePercent: null,
      price: null,
      timeLabel: null,
    };
  }

  const closePrice = profile?.richQuoteData?.close;
  const ethPrice = profile?.richQuoteData?.ethPrice;

  if (isExtendedHours(session) && !isPremarket && ethPrice === undefined) {
    return {
      change: null,
      changePercent: null,
      price: null,
      timeLabel: null,
    };
  }

  if (isPremarket && quotes) {
    const previousClosePrice = profile?.richQuoteData?.previousClosePrice;

    price = (quotes as Quote)?.price ?? quotes?.askPrice ?? profile?.richQuoteData?.lastTradePrice;
    change = quotes?.change ?? (price && previousClosePrice ? price - previousClosePrice : null);
    changePercent =
      quotes?.changePercent ?? (change && previousClosePrice ? (change / previousClosePrice) * 100 : null);
    timeLabel = profile?.richQuoteData
      ? getTimeLabels(profile.richQuoteData, session, undefined, translateFn).timestampEth
      : '-';
  } else if (isExtendedHours(session) && !isPremarket && closePrice !== undefined && ethPrice !== undefined) {
    price = ethPrice;
    change = price - closePrice;
    changePercent = (change / closePrice) * 100;
    timeLabel = profile?.richQuoteData
      ? getTimeLabels(profile.richQuoteData, session, undefined, translateFn).timestampEth
      : '-';
  } else if (ethPrice !== undefined) {
    const previousClosePrice = profile?.richQuoteData?.previousClosePrice;
    if (previousClosePrice !== undefined && profile?.richQuoteData) {
      price = ethPrice;
      change = price - previousClosePrice;
      changePercent = (change / previousClosePrice) * 100;
      timeLabel = getTimeLabels(profile.richQuoteData, session, undefined, translateFn).timestampEth;
    }
  }

  return {
    change,
    changePercent,
    price,
    timeLabel,
  };
};

export default QuoteHeaderPriceBox;
